import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  const [uploadForm, setUploadForm] = useState({
    version: '',
    platform: 'android',
    releaseNotes: '',
    file: null
  });

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      loadVersions();
    }
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      const response = await adminAPI.getProjects();
      setProjects(response.data.projects);
      setLoading(false);
    } catch (error) {
      alert('Failed to load projects');
      setLoading(false);
    }
  };

  const loadVersions = async () => {
    try {
      const response = await adminAPI.getVersions(selectedProject);
      setVersions(response.data.versions);
    } catch (error) {
      alert('Failed to load versions');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({ ...uploadForm, file });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file) {
      alert('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('version', uploadForm.version);
    formData.append('platform', uploadForm.platform);
    formData.append('releaseNotes', uploadForm.releaseNotes);

    setUploading(true);

    try {
      await adminAPI.uploadVersion(selectedProject, formData);
      alert('Version uploaded successfully!');
      setUploadForm({
        version: '',
        platform: 'android',
        releaseNotes: '',
        file: null
      });
      document.getElementById('fileInput').value = '';
      loadVersions();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to upload version');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (versionId, version) => {
    if (!window.confirm(`Delete version ${version}?`)) return;
    
    try {
      await adminAPI.deleteVersion(versionId);
      alert('Version deleted successfully!');
      loadVersions();
    } catch (error) {
      alert('Failed to delete version');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <h1 style={{ marginBottom: '24px' }}>Manage Projects & Uploads</h1>

      {/* Project Selector */}
      <div style={{ 
        background: 'white', 
        padding: '24px', 
        borderRadius: '12px', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '24px'
      }}>
        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '15px' }}>
          Select Project
        </label>
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            border: '1px solid #cbd5e0',
            borderRadius: '6px',
            fontSize: '15px',
            background: 'white'
          }}
        >
          <option value="">-- Choose a project --</option>
          {projects.map(project => (
            <option key={project.id} value={project.id}>
              {project.title}
            </option>
          ))}
        </select>
      </div>

      {selectedProject && (
        <>
          {/* Upload Form */}
          <div style={{ 
            background: 'white', 
            padding: '24px', 
            borderRadius: '12px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '24px'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Upload New Version</h2>
            <form onSubmit={handleUpload}>
              <div className="form-grid two-col" style={{ marginBottom: '16px' }}>
                  <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Version Number *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., 1.0.0"
                    value={uploadForm.version}
                    onChange={(e) => setUploadForm({ ...uploadForm, version: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #cbd5e0',
                      borderRadius: '6px',
                      fontSize: '15px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Platform *
                  </label>
                  <select
                    required
                    value={uploadForm.platform}
                    onChange={(e) => setUploadForm({ ...uploadForm, platform: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #cbd5e0',
                      borderRadius: '6px',
                      fontSize: '15px',
                      background: 'white'
                    }}
                  >
                    <option value="android">Android</option>
                    <option value="ios">iOS</option>
                    <option value="web">Web</option>
                    <option value="desktop">Desktop</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Release Notes
                </label>
                <textarea
                  rows="4"
                  placeholder="What's new in this version..."
                  value={uploadForm.releaseNotes}
                  onChange={(e) => setUploadForm({ ...uploadForm, releaseNotes: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '15px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  App File *
                </label>
                <input
                  type="file"
                  id="fileInput"
                  required
                  onChange={handleFileChange}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px dashed #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '15px'
                  }}
                />
                {uploadForm.file && (
                  <p style={{ marginTop: '8px', color: '#718096', fontSize: '14px' }}>
                    Selected: {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={uploading}
                style={{
                  padding: '12px 32px',
                  background: uploading ? '#cbd5e0' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '15px'
                }}
              >
                {uploading ? 'Uploading...' : 'Upload Version'}
              </button>
            </form>
          </div>

          {/* Versions List */}
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
              <h2 style={{ margin: 0 }}>Existing Versions</h2>
            </div>

            {versions.length === 0 ? (
              <div style={{ padding: '48px', textAlign: 'center', color: '#718096' }}>
                No versions uploaded yet
              </div>
            ) : (
                <div className="table-responsive">
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Version</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Platform</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>File Name</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Size</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Uploaded</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>By</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map(version => (
                    <tr key={version.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td data-label="Version" style={{ padding: '16px', fontWeight: '600' }}>{version.version}</td>
                      <td data-label="Platform" style={{ padding: '16px' }}>
                        <span style={{
                          padding: '4px 12px',
                          background: 
                            version.platform === 'android' ? '#d1fae5' :
                            version.platform === 'ios' ? '#dbeafe' :
                            version.platform === 'web' ? '#fef3c7' : '#e9d5ff',
                          color:
                            version.platform === 'android' ? '#065f46' :
                            version.platform === 'ios' ? '#1e40af' :
                            version.platform === 'web' ? '#92400e' : '#6b21a8',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: '500',
                          textTransform: 'capitalize'
                        }}>
                          {version.platform}
                        </span>
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px' }}>{version.file_name}</td>
                      <td data-label="Size" style={{ padding: '16px', fontSize: '14px' }}>{formatFileSize(version.file_size)}</td>
                      <td data-label="Uploaded" style={{ padding: '16px', fontSize: '14px' }}>{formatDate(version.created_at)}</td>
                      <td data-label="By" style={{ padding: '16px', fontSize: '14px' }}>{version.uploader_name}</td>
                      <td data-label="Actions" style={{ padding: '16px' }}>
                        <button
                          onClick={() => handleDelete(version.id, version.version)}
                          style={{
                            padding: '6px 12px',
                            background: '#f56565',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
                </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
